import React, { useState, useEffect } from 'react';
import { 
  Phone, 
  PhoneCall, 
  PhoneIncoming, 
  PhoneOutgoing, 
  PhoneOff, 
  Settings, 
  TestTube, 
  Activity, 
  BarChart3, 
  Users, 
  Clock, 
  Shield, 
  AlertTriangle, 
  CheckCircle, 
  XCircle, 
  Play, 
  Pause, 
  RotateCcw, 
  Plus, 
  Edit, 
  Trash2, 
  Download, 
  Upload, 
  Save, 
  Eye, 
  EyeOff,
  Mic,
  MicOff,
  Volume2,
  VolumeX,
  Headphones,
  Monitor,
  Smartphone,
  Wifi,
  WifiOff,
  Zap,
  Target,
  TrendingUp,
  TrendingDown,
  Calendar,
  MapPin,
  Globe,
  Database,
  Server,
  HardDrive,
  Cpu,
  Network,
  Mail,
  MessageSquare,
  FileText,
  Lock,
  Unlock,
  Key,
  ShieldCheck,
  AlertCircle,
  Info,
  HelpCircle,
  ExternalLink,
  Copy,
  RefreshCw,
  Filter,
  Search,
  MoreHorizontal,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  ChevronLeft,
  ArrowUp,
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  Star,
  Heart,
  ThumbsUp,
  ThumbsDown,
  Award,
  Trophy,
  Medal,
  Gift,
  Coffee,
  Pizza,
  Cake,
  Music,
  Camera,
  Video,
  File,
  Bookmark,
  Flag,
  Bell,
  Minus,
  Sort,
  MoreVertical,
  Home,
  Menu,
  X,
  Check,
  XCircle as XCircleIcon,
  CheckCircle2,
  Info as InfoIcon,
  HelpCircle as HelpCircleIcon,
  ExternalLink as ExternalLinkIcon,
  Copy as CopyIcon,
  RefreshCw as RefreshCwIcon
} from 'lucide-react';
import MockDataService from '../../services/MockDataService';

const TelephonyConfiguration = () => {
  const [telephonyData, setTelephonyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('providers');
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [showProviderForm, setShowProviderForm] = useState(false);
  const [showRoutingForm, setShowRoutingForm] = useState(false);
  const [testingProvider, setTestingProvider] = useState(null);
  const [newProvider, setNewProvider] = useState({
    name: '',
    type: 'cloud',
    configuration: {
      accountSid: '',
      authToken: '',
      phoneNumber: '',
      webhookUrl: '',
      region: 'us-east-1'
    },
    features: []
  });
  const [newRoutingRule, setNewRoutingRule] = useState({
    name: '',
    priority: 1,
    conditions: {
      timeRange: '',
      days: [],
      timezone: 'America/New_York'
    },
    actions: {
      routeTo: '',
      ringTimeout: 30,
      fallbackTo: 'voicemail'
    }
  });

  useEffect(() => {
    loadTelephonyData();
  }, []);

  const loadTelephonyData = async () => {
    try {
      setLoading(true);
      const response = await MockDataService.getTelephonyConfiguration();
      if (response.success) {
        setTelephonyData(response.data);
      }
    } catch (error) {
      console.error('Error loading telephony data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTestProvider = async (providerId) => {
    try {
      setTestingProvider(providerId);
      const response = await MockDataService.testTelephonyProvider(providerId);
      if (response.success) {
        alert('Provider test completed successfully!');
        loadTelephonyData();
      }
    } catch (error) {
      console.error('Error testing provider:', error);
      alert('Provider test failed!');
    } finally {
      setTestingProvider(null);
    }
  };

  const handleCreateProvider = async () => {
    try {
      await MockDataService.updateTelephonyProvider('new', newProvider);
      setShowProviderForm(false);
      setNewProvider({
        name: '',
        type: 'cloud',
        configuration: {
          accountSid: '',
          authToken: '',
          phoneNumber: '',
          webhookUrl: '',
          region: 'us-east-1'
        },
        features: []
      });
      loadTelephonyData();
    } catch (error) {
      console.error('Error creating provider:', error);
    }
  };

  const handleCreateRoutingRule = async () => {
    try {
      await MockDataService.createCallRoutingRule(newRoutingRule);
      setShowRoutingForm(false);
      setNewRoutingRule({
        name: '',
        priority: 1,
        conditions: {
          timeRange: '',
          days: [],
          timezone: 'America/New_York'
        },
        actions: {
          routeTo: '',
          ringTimeout: 30,
          fallbackTo: 'voicemail'
        }
      });
      loadTelephonyData();
    } catch (error) {
      console.error('Error creating routing rule:', error);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-green-600 bg-green-50';
      case 'inactive': return 'text-red-600 bg-red-50';
      case 'testing': return 'text-yellow-600 bg-yellow-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const getTestStatusIcon = (status) => {
    switch (status) {
      case 'success': return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'failed': return <XCircle className="w-4 h-4 text-red-500" />;
      case 'testing': return <RefreshCw className="w-4 h-4 text-yellow-500 animate-spin" />;
      default: return <Clock className="w-4 h-4 text-gray-500" />;
    }
  };

  const getAlertTypeColor = (type) => {
    switch (type) {
      case 'critical': return 'border-red-500 bg-red-50';
      case 'warning': return 'border-yellow-500 bg-yellow-50';
      case 'info': return 'border-blue-500 bg-blue-50';
      default: return 'border-gray-500 bg-gray-50';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!telephonyData) {
    return (
      <div className="text-center py-8">
        <Phone className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-500">Failed to load telephony configuration</p>
      </div>
    );
  }

  // Safely extract data with defaults
  const providers = telephonyData.providers || [];
  const routingRules = telephonyData.callRouting?.rules || [];
  const routingQueues = telephonyData.callRouting?.queues || [];
  const recordingPolicies = telephonyData.callRecording?.policies || [];
  const analyticsTrends = telephonyData.analytics?.trends || [];
  const monitoringAlerts = telephonyData.monitoring?.alerts || [];
  const webhookEndpoints = telephonyData.integration?.webhookIntegration?.endpoints || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Telephony Configuration</h1>
          <p className="text-gray-600">Manage phone system providers, routing, and call settings</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => setShowProviderForm(true)}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
             style={{ background: 'linear-gradient(135deg,#006600 0%,#BB0000 100%)' }}
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Provider
          </button>
        </div>
      </div>

      {/* System Health Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-6 rounded-lg border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">System Status</p>
              <p className={`text-2xl font-bold ${(telephonyData.monitoring?.systemHealth?.status || 'unknown') === 'healthy' ? 'text-green-600' : 'text-red-600'}`}>
                {(telephonyData.monitoring?.systemHealth?.status || 'unknown') === 'healthy' ? 'Healthy' : 'Issues'}
              </p>
            </div>
            <div className={`p-3 rounded-full ${(telephonyData.monitoring?.systemHealth?.status || 'unknown') === 'healthy' ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-600'}`}>
              <Shield className="w-6 h-6" />
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Uptime: {telephonyData.monitoring?.systemHealth?.uptime || 0}%
          </p>
        </div>

        <div className="bg-white p-6 rounded-lg border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Calls</p>
              <p className="text-2xl font-bold text-blue-600">{telephonyData.analytics?.callMetrics?.totalCalls || 0}</p>
            </div>
            <div className="p-3 rounded-full bg-blue-50 text-blue-600">
              <PhoneCall className="w-6 h-6" />
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Today: {telephonyData.analytics?.callMetrics?.callVolume?.today || 0}
          </p>
        </div>

        <div className="bg-white p-6 rounded-lg border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Answer Rate</p>
              <p className="text-2xl font-bold text-green-600">{telephonyData.analytics?.performanceMetrics?.answerRate || 0}%</p>
            </div>
            <div className="p-3 rounded-full bg-green-50 text-green-600">
              <CheckCircle className="w-6 h-6" />
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Service Level: {telephonyData.analytics?.performanceMetrics?.serviceLevel || 0}%
          </p>
        </div>

        <div className="bg-white p-6 rounded-lg border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Call Quality</p>
              <p className="text-2xl font-bold text-purple-600">{telephonyData.monitoring?.callQuality?.averageQuality || 0}/5</p>
            </div>
            <div className="p-3 rounded-full bg-purple-50 text-purple-600">
              <Headphones className="w-6 h-6" />
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Latency: {telephonyData.monitoring?.callQuality?.latency || 0}ms
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg border">
        <div className="border-b">
          <nav className="flex space-x-8 px-6">
            {[
              { id: 'providers', label: 'Providers', icon: Phone },
              { id: 'routing', label: 'Call Routing', icon: PhoneIncoming },
              { id: 'recording', label: 'Recording', icon: Mic },
              { id: 'analytics', label: 'Analytics', icon: BarChart3 },
              { id: 'monitoring', label: 'Monitoring', icon: Activity },
              { id: 'integration', label: 'Integration', icon: Database }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-[#006600] text-[#006600]'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <tab.icon className="w-4 h-4 mr-2" />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {/* Providers Tab */}
          {activeTab === 'providers' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold">Telephony Providers</h3>
                <button
                  onClick={() => setShowProviderForm(true)}
                  className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                 style={{ background: 'linear-gradient(135deg,#006600 0%,#BB0000 100%)' }}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Provider
                </button>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {providers.length > 0 ? providers.map(provider => (
                  <div key={provider.id} className="border rounded-lg p-6">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h4 className="text-lg font-medium">{provider.name}</h4>
                        <p className="text-sm text-gray-600 capitalize">{provider.type} Provider</p>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(provider.status)}`}>
                          {provider.status}
                        </span>
                        {getTestStatusIcon(provider.testStatus)}
                      </div>
                    </div>

                    <div className="space-y-3">
                      <div>
                        <p className="text-sm font-medium text-gray-700">Phone Number</p>
                        <p className="text-sm text-gray-600">{provider.configuration.phoneNumber}</p>
                      </div>
                      
                      <div>
                        <p className="text-sm font-medium text-gray-700">Features</p>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {provider.features && provider.features.length > 0 ? provider.features.map(feature => (
                            <span key={feature} className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                              {feature}
                            </span>
                          )) : (
                            <span className="text-xs text-gray-500">No features configured</span>
                          )}
                        </div>
                      </div>

                      <div>
                        <p className="text-sm font-medium text-gray-700">Pricing</p>
                        <div className="text-sm text-gray-600">
                          <p>Voice: ${provider.pricing.voicePerMinute}/min</p>
                          <p>SMS: ${provider.pricing.smsPerMessage}/msg</p>
                        </div>
                      </div>

                      <div className="flex space-x-2 pt-4">
                        <button
                          onClick={() => handleTestProvider(provider.id)}
                          disabled={testingProvider === provider.id}
                          className="flex items-center px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                        >
                          <TestTube className="w-4 h-4 mr-2" />
                          Test
                        </button>
                        <button
                          onClick={() => setSelectedProvider(provider)}
                          className="flex items-center px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                        >
                          <Edit className="w-4 h-4 mr-2" />
                          Edit
                        </button>
                      </div>
                    </div>
                  </div>
                )) : (
                  <div className="col-span-2 text-center py-8 text-gray-500">
                    <p>No providers configured</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Call Routing Tab */}
          {activeTab === 'routing' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold">Call Routing Rules</h3>
                <button
                  onClick={() => setShowRoutingForm(true)}
                  className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                   style={{ background: 'linear-gradient(135deg,#006600 0%,#BB0000 100%)' }}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Rule
                </button>
              </div>

              <div className="space-y-4">
                {routingRules.length > 0 ? routingRules.map(rule => (
                  <div key={rule.id} className="border rounded-lg p-4">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <h4 className="font-medium">{rule.name}</h4>
                          <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                            Priority {rule.priority}
                          </span>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(rule.status)}`}>
                            {rule.status}
                          </span>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                          <div>
                            <p className="font-medium text-gray-700">Conditions</p>
                            <p className="text-gray-600">
                              {rule.conditions.timeRange && `Time: ${rule.conditions.timeRange}`}
                              {rule.conditions.days && ` | Days: ${rule.conditions.days.join(', ')}`}
                              {rule.conditions.timezone && ` | TZ: ${rule.conditions.timezone}`}
                            </p>
                          </div>
                          <div>
                            <p className="font-medium text-gray-700">Actions</p>
                            <p className="text-gray-600">
                              Route to: {rule.actions.routeTo} | 
                              Timeout: {rule.actions.ringTimeout}s | 
                              Fallback: {rule.actions.fallbackTo}
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <button className="p-2 text-blue-600 hover:bg-blue-50 rounded">
                          <Edit className="w-4 h-4" />
                        </button>
                        <button className="p-2 text-red-600 hover:bg-red-50 rounded">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                )) : (
                  <div className="text-center py-8 text-gray-500">
                    <p>No routing rules configured</p>
                  </div>
                )}
              </div>

              {/* Call Queues */}
              <div className="mt-8">
                <h3 className="text-lg font-semibold mb-4">Call Queues</h3>
                <div className="space-y-4">
                  {routingQueues.length > 0 ? routingQueues.map(queue => (
                    <div key={queue.id} className="border rounded-lg p-4">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <h4 className="font-medium">{queue.name}</h4>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm mt-2">
                            <div>
                              <p className="font-medium text-gray-700">Strategy</p>
                              <p className="text-gray-600 capitalize">{queue.strategy?.replace('_', ' ') || 'N/A'}</p>
                            </div>
                            <div>
                              <p className="font-medium text-gray-700">Members</p>
                              <p className="text-gray-600">{(queue.members && queue.members.length) || 0} agents</p>
                            </div>
                            <div>
                              <p className="font-medium text-gray-700">Max Wait</p>
                              <p className="text-gray-600">{queue.maxWaitTime}s</p>
                            </div>
                          </div>
                        </div>
                        <div className="flex space-x-2">
                          <button className="p-2 text-blue-600 hover:bg-blue-50 rounded">
                            <Edit className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  )) : (
                    <div className="text-center py-8 text-gray-500">
                      <p>No call queues configured</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Recording Tab */}
          {activeTab === 'recording' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold">Call Recording Settings</h3>
                <div className="flex items-center space-x-2">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${telephonyData.callRecording?.enabled ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                    {telephonyData.callRecording?.enabled ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h4 className="font-medium">Recording Settings</h4>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm">Record All Calls</span>
                      <input type="checkbox" checked={telephonyData.callRecording?.settings?.recordAllCalls || false} readOnly />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm">Record Internal Calls</span>
                      <input type="checkbox" checked={telephonyData.callRecording?.settings?.recordInternalCalls || false} readOnly />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm">Record External Calls</span>
                      <input type="checkbox" checked={telephonyData.callRecording?.settings?.recordExternalCalls || false} readOnly />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm">Encryption</span>
                      <input type="checkbox" checked={telephonyData.callRecording?.settings?.encryption || false} readOnly />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm">Consent Required</span>
                      <input type="checkbox" checked={telephonyData.callRecording?.settings?.consentRequired || false} readOnly />
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <h4 className="font-medium">Storage & Retention</h4>
                  <div className="space-y-3">
                    <div>
                      <p className="text-sm font-medium text-gray-700">Storage Location</p>
                      <p className="text-sm text-gray-600 capitalize">{telephonyData.callRecording?.settings?.storageLocation || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-700">Retention Period</p>
                      <p className="text-sm text-gray-600">{telephonyData.callRecording?.settings?.retentionPeriod || 0} days</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Recording Policies */}
              <div className="mt-6">
                <h4 className="font-medium mb-4">Recording Policies</h4>
                <div className="space-y-3">
                  {recordingPolicies.length > 0 ? recordingPolicies.map(policy => (
                    <div key={policy.id} className="border rounded-lg p-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <h5 className="font-medium">{policy.name}</h5>
                          <p className="text-sm text-gray-600 mt-1">{policy.description}</p>
                          <p className="text-sm text-gray-500 mt-2">{policy.consentText}</p>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${policy.required ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'}`}>
                            {policy.required ? 'Required' : 'Optional'}
                          </span>
                        </div>
                      </div>
                    </div>
                  )) : (
                    <div className="text-center py-4 text-gray-500">
                      <p>No recording policies configured</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Analytics Tab */}
          {activeTab === 'analytics' && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold">Call Analytics</h3>
              
              {/* Performance Metrics */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white p-4 rounded-lg border">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Average Duration</p>
                      <p className="text-xl font-bold text-blue-600">
                        {telephonyData.analytics?.callMetrics?.averageCallDuration ? (
                          <>
                            {Math.floor(telephonyData.analytics.callMetrics.averageCallDuration / 60)}m {telephonyData.analytics.callMetrics.averageCallDuration % 60}s
                          </>
                        ) : 'N/A'}
                      </p>
                    </div>
                    <Clock className="w-8 h-8 text-blue-600" />
                  </div>
                </div>

                <div className="bg-white p-4 rounded-lg border">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Average Wait Time</p>
                      <p className="text-xl font-bold text-orange-600">{telephonyData.analytics?.callMetrics?.averageWaitTime || 0}s</p>
                    </div>
                    <Clock className="w-8 h-8 text-orange-600" />
                  </div>
                </div>

                <div className="bg-white p-4 rounded-lg border">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">First Call Resolution</p>
                      <p className="text-xl font-bold text-green-600">{telephonyData.analytics?.performanceMetrics?.firstCallResolution || 0}%</p>
                    </div>
                    <Target className="w-8 h-8 text-green-600" />
                  </div>
                </div>
              </div>

              {/* Call Volume Trends */}
              <div className="bg-white p-6 rounded-lg border">
                <h4 className="font-medium mb-4">Call Volume Trends (Last 20 Days)</h4>
                <div className="space-y-2">
                  {analyticsTrends.length > 0 ? analyticsTrends.slice(-10).map((trend, index) => (
                    <div key={index} className="flex items-center justify-between py-2 border-b">
                      <span className="text-sm text-gray-600">{new Date(trend.date).toLocaleDateString()}</span>
                      <div className="flex items-center space-x-4">
                        <span className="text-sm">Total: {trend.calls}</span>
                        <span className="text-sm text-green-600">Answered: {trend.answered}</span>
                        <span className="text-sm text-red-600">Missed: {trend.missed}</span>
                      </div>
                    </div>
                  )) : (
                    <div className="text-center py-4 text-gray-500">
                      <p>No trend data available</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Monitoring Tab */}
          {activeTab === 'monitoring' && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold">System Monitoring</h3>
              
              {/* System Health */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white p-6 rounded-lg border">
                  <h4 className="font-medium mb-4">System Health</h4>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-sm">Status</span>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(telephonyData.monitoring?.systemHealth?.status || 'unknown')}`}>
                        {telephonyData.monitoring?.systemHealth?.status || 'unknown'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm">Uptime</span>
                      <span className="text-sm font-medium">{telephonyData.monitoring?.systemHealth?.uptime || 0}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm">Last Check</span>
                      <span className="text-sm">{telephonyData.monitoring?.systemHealth?.lastCheck ? new Date(telephonyData.monitoring.systemHealth.lastCheck).toLocaleString() : 'N/A'}</span>
                    </div>
                  </div>
                </div>

                <div className="bg-white p-6 rounded-lg border">
                  <h4 className="font-medium mb-4">Call Quality</h4>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-sm">Average Quality</span>
                      <span className="text-sm font-medium">{telephonyData.monitoring?.callQuality?.averageQuality || 0}/5</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm">Jitter</span>
                      <span className="text-sm font-medium">{telephonyData.monitoring?.callQuality?.jitter || 0}ms</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm">Packet Loss</span>
                      <span className="text-sm font-medium">{telephonyData.monitoring?.callQuality?.packetLoss || 0}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm">Latency</span>
                      <span className="text-sm font-medium">{telephonyData.monitoring?.callQuality?.latency || 0}ms</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Alerts */}
              <div className="bg-white rounded-lg border">
                <div className="p-6 border-b">
                  <h4 className="font-medium">System Alerts</h4>
                </div>
                <div className="p-6">
                  <div className="space-y-3">
                    {monitoringAlerts.length > 0 ? monitoringAlerts.map(alert => (
                      <div key={alert.id} className={`p-4 rounded-lg border-l-4 ${getAlertTypeColor(alert.type)}`}>
                        <div className="flex justify-between items-start">
                          <div>
                            <h5 className="font-medium">{alert.title}</h5>
                            <p className="text-sm text-gray-600 mt-1">{alert.description}</p>
                            <p className="text-xs text-gray-500 mt-2">
                              {new Date(alert.timestamp).toLocaleString()}
                            </p>
                          </div>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            alert.status === 'active' ? 'bg-red-100 text-red-800' : 'bg-blue-100 text-blue-800'
                          }`}>
                            {alert.status}
                          </span>
                        </div>
                      </div>
                    )) : (
                      <div className="text-center py-4 text-gray-500">
                        <p>No alerts</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Integration Tab */}
          {activeTab === 'integration' && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold">System Integration</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* CRM Integration */}
                <div className="bg-white p-6 rounded-lg border">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-medium">CRM Integration</h4>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${telephonyData.integration?.crmIntegration?.enabled ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                      {telephonyData.integration?.crmIntegration?.enabled ? 'Enabled' : 'Disabled'}
                    </span>
                  </div>
                  <div className="space-y-3 text-sm">
                    <div>
                      <p className="font-medium text-gray-700">Provider</p>
                      <p className="text-gray-600">{telephonyData.integration?.crmIntegration?.provider || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="font-medium text-gray-700">Auto Create Contacts</p>
                      <p className="text-gray-600">{telephonyData.integration?.crmIntegration?.autoCreateContacts ? 'Yes' : 'No'}</p>
                    </div>
                    <div>
                      <p className="font-medium text-gray-700">Last Sync</p>
                      <p className="text-gray-600">{telephonyData.integration?.crmIntegration?.lastSync ? new Date(telephonyData.integration.crmIntegration.lastSync).toLocaleString() : 'N/A'}</p>
                    </div>
                  </div>
                </div>

                {/* Ticketing Integration */}
                <div className="bg-white p-6 rounded-lg border">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-medium">Ticketing Integration</h4>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${telephonyData.integration?.ticketingIntegration?.enabled ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                      {telephonyData.integration?.ticketingIntegration?.enabled ? 'Enabled' : 'Disabled'}
                    </span>
                  </div>
                  <div className="space-y-3 text-sm">
                    <div>
                      <p className="font-medium text-gray-700">Auto Create Tickets</p>
                      <p className="text-gray-600">{telephonyData.integration?.ticketingIntegration?.autoCreateTickets ? 'Yes' : 'No'}</p>
                    </div>
                    <div>
                      <p className="font-medium text-gray-700">Ticket Template</p>
                      <p className="text-gray-600">{telephonyData.integration?.ticketingIntegration?.ticketTemplate || 'N/A'}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Webhook Integration */}
              <div className="bg-white p-6 rounded-lg border">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-medium">Webhook Integration</h4>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${telephonyData.integration?.webhookIntegration?.enabled ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                    {telephonyData.integration?.webhookIntegration?.enabled ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
                <div className="space-y-3">
                  {webhookEndpoints.length > 0 ? webhookEndpoints.map((endpoint, index) => (
                    <div key={index} className="border rounded-lg p-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium text-sm">{endpoint.url}</p>
                          <p className="text-sm text-gray-600 mt-1">
                            Events: {endpoint.events.join(', ')}
                          </p>
                        </div>
                        <button className="p-2 text-blue-600 hover:bg-blue-50 rounded">
                          <Edit className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  )) : (
                    <div className="text-center py-4 text-gray-500">
                      <p>No webhook endpoints configured</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Provider Form Modal */}
      {showProviderForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Add Telephony Provider</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Provider Name</label>
                <input
                  type="text"
                  value={newProvider.name}
                  onChange={(e) => setNewProvider({...newProvider, name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="Enter provider name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                <select
                  value={newProvider.type}
                  onChange={(e) => setNewProvider({...newProvider, type: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="cloud">Cloud</option>
                  <option value="on-premise">On-Premise</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                <input
                  type="text"
                  value={newProvider.configuration.phoneNumber}
                  onChange={(e) => setNewProvider({...newProvider, configuration: {...newProvider.configuration, phoneNumber: e.target.value}})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="+1234567890"
                />
              </div>
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowProviderForm(false)}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateProvider}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                 style={{ background: 'linear-gradient(135deg,#006600 0%,#BB0000 100%)' }}
              >
                Add Provider
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Routing Rule Form Modal */}
      {showRoutingForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Add Call Routing Rule</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Rule Name</label>
                <input
                  type="text"
                  value={newRoutingRule.name}
                  onChange={(e) => setNewRoutingRule({...newRoutingRule, name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="Enter rule name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                <input
                  type="number"
                  value={newRoutingRule.priority}
                  onChange={(e) => setNewRoutingRule({...newRoutingRule, priority: parseInt(e.target.value)})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  min="1"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Route To</label>
                <select
                  value={newRoutingRule.actions.routeTo}
                  onChange={(e) => setNewRoutingRule({...newRoutingRule, actions: {...newRoutingRule.actions, routeTo: e.target.value}})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="">Select destination</option>
                  <option value="support_team">Support Team</option>
                  <option value="emergency_team">Emergency Team</option>
                  <option value="voicemail">Voicemail</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowRoutingForm(false)}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateRoutingRule}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                 style={{ background: 'linear-gradient(135deg,#006600 0%,#BB0000 100%)' }}
              >
                Add Rule
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TelephonyConfiguration;
